import React, { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { FiMail, FiLock, FiArrowRight, FiEye, FiEyeOff } from "react-icons/fi";
import { FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import { FaBuilding } from "react-icons/fa";

const ClientLogin = () => {
    const [formData, setFormData] = useState({ 
        email: "", 
        password: "" 
    });
    const [otpData, setOtpData] = useState({
        otp: "",
        clientId: "",
        email: ""
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [showOtpScreen, setShowOtpScreen] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleOtpChange = (e) => {
        // Only allow numbers
        const value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length <= 6) {
            setOtpData({ ...otpData, otp: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ text: "", type: "" });

        try {
            const res = await axios.post(`https://api.ingrainsystems.com/api/clients/clientlogin`, formData);
            
            console.log("Login response:", res.data);
            
            if (res.data.success) {
                // Store clientId and email for OTP verification
                setOtpData({
                    otp: "",
                    clientId: res.data.clientId || "",
                    email: res.data.email || formData.email
                });
                
                setMessage({ 
                    text: "OTP sent to your email! Please verify to continue.", 
                    type: "success" 
                });
                
                // Show OTP screen after successful login
                setShowOtpScreen(true);
            } else {
                setMessage({
                    text: res.data.message || "Login failed. Please try again.",
                    type: "error"
                });
            }
        } catch (err) {
            console.error("Login error:", err);
            setMessage({
                text: err.response?.data?.message || "Invalid credentials. Please try again.",
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOtpVerify = async (e) => {
        e.preventDefault();
        setOtpLoading(true);
        setMessage({ text: "", type: "" });

        try {
            const payload = {
                otp: otpData.otp
            };
            
            // Add either clientId or email based on what's available
            if (otpData.clientId) {
                payload.clientId = otpData.clientId;
            } else {
                payload.email = otpData.email;
            }
            
            const res = await axios.post(`https://api.ingrainsystems.com/api/clients/verify-client-otp`, payload);
            
            console.log("OTP verification response:", res.data);
            
            // Check if response has success and contains token and client data
            if (res.data.success && res.data.token && res.data.client) {
                // Store token and client data in localStorage
                localStorage.setItem("clientToken", res.data.token);
                localStorage.setItem("clientId", res.data.client._id);
                localStorage.setItem("clientCustomId", res.data.client.clientId);
                localStorage.setItem("clientName", res.data.client.name);
                localStorage.setItem("clientEmail", res.data.client.email);
                localStorage.setItem("clientMobile", res.data.client.mobile);
                localStorage.setItem("companyName", res.data.client.companyName);
                localStorage.setItem("employeesCount", res.data.client.employeesCount);
                localStorage.setItem("accessibleProducts", JSON.stringify(res.data.client.accessibleProducts));
                localStorage.setItem("userRole", "client");
                localStorage.setItem("clientData", JSON.stringify(res.data.client));

                setMessage({ text: "Login successful! Redirecting to dashboard...", type: "success" });
                
                // Navigate to recruitment-dashboard for client (not candidate-dashboard)
                setTimeout(() => {
                    navigate("/recruitment-dashboard");
                }, 1500);
            } else {
                setMessage({
                    text: res.data.message || "Invalid OTP. Please try again.",
                    type: "error"
                });
            }
        } catch (err) {
            console.error("OTP verification error:", err);
            setMessage({
                text: err.response?.data?.message || "OTP verification failed. Please try again.",
                type: "error"
            });
        } finally {
            setOtpLoading(false);
        }
    };

    const handleResendOtp = async () => {
        setLoading(true);
        setMessage({ text: "", type: "" });
        
        try {
            const res = await axios.post(`https://api.ingrainsystems.com/api/clients/clientlogin`, {
                email: formData.email,
                password: formData.password
            });
            
            if (res.data.success) {
                setMessage({ 
                    text: "New OTP sent to your email!", 
                    type: "success" 
                });
                
                // Clear existing OTP field
                setOtpData({ ...otpData, otp: "" });
            } else {
                setMessage({
                    text: res.data.message || "Failed to resend OTP.",
                    type: "error"
                });
            }
        } catch (err) {
            console.error("Resend OTP error:", err);
            setMessage({
                text: err.response?.data?.message || "Failed to resend OTP. Please try again.",
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        setShowOtpScreen(false);
        setOtpData({ otp: "", clientId: "", email: "" });
        setMessage({ text: "", type: "" });
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 font-sans">
            <div className="w-full max-w-5xl bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-100/50 overflow-hidden flex flex-col md:flex-row border border-slate-100 animate-in fade-in zoom-in duration-500">

                {/* Visual Side - Always Visible */}
                <div className="md:w-1/2 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 p-12 text-white flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.15)_0%,transparent_60%)]"></div>
                    <div className="absolute bottom-[-10%] right-[-10%] w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>

                    <div className="relative z-10">
                        <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-10 backdrop-blur-md border border-white/30 shadow-xl">
                            <FaBuilding className="text-2xl" />
                        </div>
                        <h2 className="text-4xl font-black leading-tight mb-4">
                            {showOtpScreen ? "Verify Your" : "Welcome back,"} <br />
                            <span className="text-emerald-200">
                                {showOtpScreen ? "Identity" : "Business Partner"}
                            </span>
                        </h2>
                        <p className="text-lg text-emerald-100/80 font-medium max-w-xs leading-relaxed">
                            {showOtpScreen 
                                ? "Enter the 6-digit code sent to your email to complete login." 
                                : "Access your client dashboard to manage job postings and track candidates."}
                        </p>
                    </div>

                    <div className="relative z-10 pt-12">
                        <div className="flex -space-x-3 mb-6">
                            {[21, 22, 23, 24].map(i => (
                                <div key={i} className="w-10 h-10 rounded-full border-2 border-emerald-600 bg-emerald-400 flex items-center justify-center overflow-hidden">
                                    <img src={`https://i.pravatar.cc/100?img=${i}`} alt="user" className="w-full h-full object-cover" />
                                </div>
                            ))}
                            <div className="w-10 h-10 rounded-full border-2 border-emerald-600 bg-emerald-800 flex items-center justify-center text-[10px] font-black">
                                +500
                            </div>
                        </div>
                        <p className="text-[10px] font-black text-emerald-200/60 uppercase tracking-[0.2em]">
                            Trusted by 500+ businesses
                        </p>
                    </div>
                </div>

                {/* Form Side - Changes based on OTP screen */}
                <div className="md:w-1/2 p-12 md:p-20 flex flex-col justify-center bg-white">
                    {!showOtpScreen ? (
                        // Login Form
                        <>
                            <div className="mb-12">
                                <h1 className="text-2xl font-black text-slate-800 mb-1">Client Login</h1>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                    Access your business account
                                </p>
                            </div>

                            {message.text && (
                                <div className={`mb-8 p-4 rounded-xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 border ${message.type === "success"
                                    ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                    : "bg-rose-50 text-rose-600 border-rose-100"
                                    }`}>
                                    {message.type === "success" ? <FiCheckCircle className="text-lg shrink-0" /> : <FiAlertCircle className="text-lg shrink-0" />}
                                    <span className="text-sm font-bold">{message.text}</span>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-600 transition-colors">
                                            <FiMail className="text-lg" />
                                        </div>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="w-full pl-11 pr-4 py-4 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-emerald-600/5 focus:border-emerald-600 outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm shadow-slate-100/50"
                                            placeholder="company@example.com"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Password</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-600 transition-colors">
                                            <FiLock className="text-lg" />
                                        </div>
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required
                                            className="w-full pl-11 pr-12 py-4 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-emerald-600/5 focus:border-emerald-600 outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm shadow-slate-100/50"
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-emerald-600 transition-colors"
                                        >
                                            {showPassword ? <FiEyeOff className="text-lg" /> : <FiEye className="text-lg" />}
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || !formData.email || !formData.password}
                                    className="w-full py-6 bg-emerald-600 text-white font-black rounded-xl shadow-xl shadow-emerald-100 hover:bg-emerald-700 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3 disabled:opacity-70 disabled:pointer-events-none group uppercase tracking-widest text-sm"
                                >
                                    {loading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <span>Sign In</span>
                                            <FiArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )}
                                </button>
                            </form>

                            <p className="mt-12 text-center text-[11px] text-slate-400 font-bold uppercase tracking-wider">
                                Don't have a client account?{" "}
                                <Link to="/client-register" className="text-emerald-600 font-black hover:underline underline-offset-8 transition-all">
                                    Create Account
                                </Link>
                            </p>
                        </>
                    ) : (
                        // OTP Verification Form
                        <>
                            <div className="mb-8">
                                <button
                                    onClick={handleBackToLogin}
                                    className="text-[10px] font-black text-emerald-600 uppercase tracking-widest hover:underline mb-6 flex items-center gap-2 transition-all"
                                >
                                    ← Back to Login
                                </button>
                                <h1 className="text-2xl font-black text-slate-800 mb-1">Verify OTP</h1>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                    Enter verification code
                                </p>
                            </div>

                            {message.text && (
                                <div className={`mb-8 p-4 rounded-xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 border ${message.type === "success"
                                    ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                    : "bg-rose-50 text-rose-600 border-rose-100"
                                    }`}>
                                    {message.type === "success" ? <FiCheckCircle className="text-lg shrink-0" /> : <FiAlertCircle className="text-lg shrink-0" />}
                                    <span className="text-sm font-bold">{message.text}</span>
                                </div>
                            )}

                            <form onSubmit={handleOtpVerify} className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Enter OTP</label>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            name="otp"
                                            value={otpData.otp}
                                            onChange={handleOtpChange}
                                            required
                                            maxLength="6"
                                            className="w-full px-4 py-4 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-emerald-600/5 focus:border-emerald-600 outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm shadow-slate-100/50 text-center text-2xl tracking-[0.5em]"
                                            placeholder="000000"
                                        />
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2">
                                        A 6-digit OTP has been sent to {otpData.email || formData.email}
                                    </p>
                                </div>

                                <button
                                    type="submit"
                                    disabled={otpLoading || !otpData.otp || otpData.otp.length !== 6}
                                    className="w-full py-6 bg-emerald-600 text-white font-black rounded-xl shadow-xl shadow-emerald-100 hover:bg-emerald-700 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3 disabled:opacity-70 disabled:pointer-events-none group uppercase tracking-widest text-sm"
                                >
                                    {otpLoading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <span>Verify OTP</span>
                                            <FiArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )}
                                </button>

                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={handleResendOtp}
                                        disabled={loading}
                                        className="text-xs text-emerald-600 font-bold hover:underline disabled:opacity-50 transition-all"
                                    >
                                        {loading ? "Sending..." : "Resend OTP"}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientLogin;